import React, { useState, useEffect } from 'react';
import billingService from '../../services/billingService';

const formatDate = (dateString, showTime = true) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            ...(showTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {})
        }).format(date);
    } catch (e) {
        return dateString;
    }
};

const getBadgeClass = (type) => {
    switch (type) {
        case 'payment_success': return 'badge-success';
        case 'payment_failed': return 'badge-error';
        case 'subscription_created':
        case 'trial_started': return 'badge-info';
        case 'plan_change': return 'badge-warning';
        case 'subscription_canceled': return 'badge-neutral';
        default: return 'badge-neutral';
    }
};

const getActionLabel = (type) => {
    return type.replace(/_/g, ' ');
};

const BillingHistory = () => {
    const [history, setHistory] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 12, pages: 1 });
    const [loading, setLoading] = useState(true);

    const loadHistory = async (page = 1) => {
        try {
            setLoading(true);
            const data = await billingService.getHistory(page);
            setHistory(data.history || []);
            setPagination(data.pagination);
        } catch (error) {
            console.error('Failed to load billing history:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory();
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.pages) {
            loadHistory(newPage);
        }
    };

    if (loading && history.length === 0) {
        return <div className="history-section">Loading billing history...</div>;
    }

    return (
        <div className="history-section">
            <h3>Billing History</h3>

            {history.length === 0 ? (
                <div className="no-history">No billing records found.</div>
            ) : (
                <>
                    <div className="history-table-container">
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Action</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(item => (
                                    <tr key={item.id}>
                                        <td>{formatDate(item.createdAt)}</td>
                                        <td>
                                            <span className={`badge ${getBadgeClass(item.type)}`}>
                                                {getActionLabel(item.type)}
                                            </span>
                                        </td>
                                        <td>
                                            {item.amount ? `${item.amount} ${item.currency}` : '-'}
                                        </td>
                                        <td>
                                            <span className={`badge ${item.status === 'completed' ? 'badge-success' : 'badge-neutral'}`}>
                                                {item.status || 'completed'}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            {item.externalId || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination.pages > 1 && (
                        <div className="pagination">
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1 || loading}
                            >
                                Previous
                            </button>
                            <span className="pagination-info">
                                Page {pagination.page} of {pagination.pages}
                            </span>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.pages || loading}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default BillingHistory;
